import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FaClock,
  FaLayerGroup,
  FaLeaf,
  FaPlay,
  FaRedo,
  FaSearch,
  FaSync,
} from 'react-icons/fa';
import api from '../api';
import ScheduleConfigurator from '../components/ScheduleConfigurator';
import InfoTooltip from '../components/InfoTooltip';
import { buildScheduleRequest, createEmptyScheduleState } from '../utils/schedules';

const categoryBadge = (label) => {
  if (!label || label === 'Outros') return null;
  return (
    <span className="new-request-category-badge inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600 shadow-sm">
      <FaLeaf className="h-3 w-3" />
      {label}
    </span>
  );
};

export default function NewRequestTab({ openProcessRunner, refreshRequests }) {
  const [processes, setProcesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [scheduleStates, setScheduleStates] = useState({});

  const loadProcesses = useCallback(() => {
    setLoading(true);
    setError('');
    api
      .get('/processes/available')
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        const available = list.filter((process) => {
          const status = (process.status || 'published').toLowerCase();
          return status === 'published';
        });
        setProcesses(available);
      })
      .catch(() => {
        setProcesses([]);
        setError('Não foi possível carregar os processos disponíveis.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    loadProcesses();
  }, [loadProcesses]);

  const ensureScheduleState = useCallback((processId) => {
    setScheduleStates((prev) => {
      if (prev[processId]) return prev;
      return {
        ...prev,
        [processId]: createEmptyScheduleState(),
      };
    });
  }, []);

  const handleToggleExpand = useCallback(
    (processId) => {
      setExpandedId((current) => {
        const next = current === processId ? null : processId;
        if (next) {
          ensureScheduleState(next);
        }
        return next;
      });
    },
    [ensureScheduleState]
  );

  const handleStart = useCallback(
    (process) => {
      if (typeof openProcessRunner === 'function') {
        openProcessRunner(process);
      }
    },
    [openProcessRunner]
  );

  const handleScheduleChange = useCallback((processId, value) => {
    setScheduleStates((prev) => ({
      ...prev,
      [processId]: value,
    }));
  }, []);

  const handleSchedule = useCallback(
    async (process) => {
      const scheduleState = scheduleStates[process.id] || createEmptyScheduleState();
      const validation = buildScheduleRequest(scheduleState);
      if (!validation.valid || !validation.payload) {
        if (validation.reason === 'disabled') return;
        const message = validation.message || 'Preencha os dados do agendamento para continuar.';
      alert(message);
      return;
    }

    try {
      await api.post(`/processes/${process.id}/schedules`, validation.payload);
      alert('Agendamento criado com sucesso!');
      setScheduleStates((prev) => ({
        ...prev,
        [process.id]: createEmptyScheduleState(),
      }));
      setExpandedId(null);
      if (typeof refreshRequests === 'function') {
        refreshRequests();
      }
    } catch (err) {
      const msg = err?.response?.data?.error || 'Não foi possível criar o agendamento.';
      alert(msg);
    }
  }, [refreshRequests, scheduleStates]);

  const groupedProcesses = useMemo(() => {
    const normalized = searchTerm.trim().toLowerCase();
    const filtered = processes.filter((process) => {
      if (!normalized) return true;
      const haystack = `${process.name || ''} ${process.description || ''} ${process.category || ''}`.toLowerCase();
      return haystack.includes(normalized);
    });

    const sorted = [...filtered].sort((a, b) =>
      a.name.localeCompare(b.name, 'pt', { sensitivity: 'base' })
    );

    const groups = new Map();
    sorted.forEach((process) => {
      const category = process.category?.trim() || 'Outros';
      if (!groups.has(category)) {
        groups.set(category, []);
      }
      groups.get(category).push(process);
    });

    return Array.from(groups.entries()).sort((a, b) =>
      a[0].localeCompare(b[0], 'pt', { sensitivity: 'base' })
    );
  }, [processes, searchTerm]);

  const hasProcesses = processes.length > 0;
  const nothingFound = !loading && groupedProcesses.length === 0;

  return (
    <div className="new-request-tab-shell min-h-full bg-gradient-to-b from-slate-50 via-white to-slate-100">
      <div className="mx-auto flex max-w-6xl flex-col gap-8 px-4 py-8 sm:px-6 lg:px-8">
        <header className="new-request-tab-header rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="space-y-2">
              <p className="text-sm font-medium uppercase tracking-[0.2em] text-blue-700">
                Nova solicitação
              </p>
              <h1 className="flex items-center gap-2 text-3xl font-semibold text-slate-800">
                Escolha um processo
                <InfoTooltip label="Inicie agora ou agende para depois sem sair desta tela." />
              </h1>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                onClick={loadProcesses}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-blue-400 hover:text-blue-700"
              >
                <FaSync className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Atualizar lista
              </button>
            </div>
          </div>
          <div className="new-request-tab-search mt-6 flex items-center gap-3 rounded-full border border-slate-300 bg-white px-4 py-2 shadow-inner">
            <FaSearch className="h-4 w-4 text-slate-500" />
            <input
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Busque por nome, descrição ou categoria"
              className="w-full bg-transparent text-sm text-slate-800 placeholder:text-slate-500 focus:outline-none"
            />
          </div>
          {error ? (
            <p className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {error}
            </p>
          ) : null}
        </header>

        {loading ? (
          <div className="new-request-tab-state space-y-4 rounded-3xl border border-slate-200 bg-white p-6 text-sm text-slate-500 shadow-sm">
            Carregando processos disponíveis...
          </div>
        ) : null}

        {nothingFound ? (
          <div className="new-request-tab-state rounded-3xl border border-dashed border-slate-300 bg-white p-10 text-center">
            <p className="text-base font-medium text-slate-700">
              {hasProcesses
                ? 'Nenhum processo corresponde à busca realizada.'
                : 'Nenhum processo disponível no momento.'}
            </p>
            <p className="mt-2 text-sm text-slate-500">
              Ajuste os filtros ou tente novamente mais tarde.
            </p>
          </div>
        ) : null}

        <div className="space-y-10">
          {groupedProcesses.map(([category, list]) => (
            <section key={category} className="space-y-5">
              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="new-request-category-header inline-flex items-center gap-4 rounded-3xl border border-blue-100 bg-gradient-to-r from-blue-50 via-white to-blue-50 px-4 py-3 shadow-sm">
                    <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 text-white shadow-md">
                      <FaLayerGroup className="h-5 w-5" />
                    </span>
                    <div className="space-y-1">
                      <span className="text-[11px] font-semibold uppercase tracking-[0.4em] text-blue-700">Categoria</span>
                      <div className="flex items-center gap-2">
                        <h2 className="text-xl font-semibold text-slate-800 sm:text-2xl">{category}</h2>
                        <InfoTooltip
                          label={
                            list.length === 1
                              ? 'Um processo disponível nesta categoria.'
                              : `${list.length} processos disponíveis nesta categoria.`
                          }
                        />
                      </div>
                    </div>
                  </div>
                  {categoryBadge(category)}
                </div>
              </div>
              <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
                {list.map((process) => {
                  const isExpanded = expandedId === process.id;
                  const scheduleState = scheduleStates[process.id] || createEmptyScheduleState();
                  return (
                    <article
                      key={process.id}
                      className="new-request-process-card group relative flex h-full flex-col overflow-hidden rounded-3xl border border-slate-300 bg-white p-6 text-slate-700 shadow-sm transition hover:border-blue-300 hover:shadow-lg"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <h3 className="text-xl font-semibold text-slate-800 group-hover:text-blue-700">
                              {process.name}
                            </h3>
                            <InfoTooltip label={process.description || 'Sem descrição adicional.'} />
                          </div>
                        </div>
                        <span className="new-request-process-id rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-700">
                          #{process.id}
                        </span>
                      </div>
                      <div className="mt-5 flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => handleStart(process)}
                          className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700"
                        >
                          <FaPlay className="h-4 w-4" />
                          Iniciar agora
                        </button>
                        <button
                          type="button"
                          onClick={() => handleToggleExpand(process.id)}
                          className={`inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition ${
                            isExpanded
                              ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                              : 'border border-emerald-200 bg-white text-emerald-600 hover:border-emerald-400 hover:text-emerald-700'
                          }`}
                        >
                          <FaClock className="h-4 w-4" />
                          Agendar
                        </button>
                      </div>
                      {isExpanded ? (
                        <div className="new-request-schedule-panel mt-6 space-y-4 rounded-2xl border border-emerald-100 bg-emerald-50/60 p-4">
                          <ScheduleConfigurator
                            value={scheduleState}
                            onChange={(value) => handleScheduleChange(process.id, value)}
                          />
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => handleToggleExpand(process.id)}
                              className="rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-white"
                            >
                              Cancelar
                            </button>
                            <button
                              type="button"
                              onClick={() => handleSchedule(process)}
                              disabled={!scheduleState.enabled}
                              className="inline-flex items-center justify-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              <FaRedo className="h-4 w-4" />
                              Confirmar agendamento
                            </button>
                          </div>
                        </div>
                      ) : null}
                    </article>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
